import React, { useState, useRef } from 'react';
import ZentorgaForm from '../components/ZentorgaForm';
import SurveyQuestion from '../components/SurveyQuestion';
import SoundVideo from '../components/SoundVideo';
import BenefitPeriod from '../../components/BenefitPeriod';
import { useFormSubmit } from '../hooks/useFormSubmit';
import { getImageArray } from '../utils/templateMapper';
import '../styles/zentorga.scss';
import '../styles/template13.scss';

/**
 * Template13 - 단일 단계 폼 + 2개 설문
 * 구조: 이미지 → 설문1 → 설문2 → 혜택기간(선택) → 폼 → 버튼 → 서비스정보(선택) → 푸터
 */
const Template13 = ({
  landingData,
  urlCode,
  urlCodeSettingId
}) => {
  const { handleSubmit, isSubmitting } = useFormSubmit(
    urlCode,
    urlCodeSettingId,
    landingData.endpoint
  );
  const [surveyAnswer1, setSurveyAnswer1] = useState('');
  const [surveyAnswer2, setSurveyAnswer2] = useState('');

  const handleFormSubmit = async (formData) => {
    await handleSubmit({
      ...formData,
      ...(surveyAnswer1 && { selectedOption: surveyAnswer1 }),
      ...(surveyAnswer2 && { selectedOption2: surveyAnswer2 }),
    });
  };

  const formRef = useRef(null);
  // 이미지 배열 생성 (최대 6개)
  const images = getImageArray(landingData, 6);

  // 설문 1 답변 배열
  const survey1Answers = [
    landingData.surveyOpt1_1,
    landingData.surveyOpt1_2,
    landingData.surveyOpt1_3,
    landingData.surveyOpt1_4,
    landingData.surveyOpt1_5
  ].filter(Boolean);

  // 설문 2 답변 배열
  const survey2Answers = [
    landingData.surveyOpt2_1,
    landingData.surveyOpt2_2,
    landingData.surveyOpt2_3,
    landingData.surveyOpt2_4,
    landingData.surveyOpt2_5
  ].filter(Boolean);

  return (
    <section className="template13-layout zentorga-layout">
      {/* 이미지 섹션 */}
      {images.length > 0 && (
        <div className="template13-images zentorga-images">
          {images.map((img, idx) => {
            const fileExtension = img.url.split('.').pop().toLowerCase();
            const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension);

            return isVideo ? (
              <SoundVideo key={idx} src={img.url} loop={img.alt !== "false"} />
            ) : (
              <img key={idx} src={img.url} alt={img.alt} />
            );
          })}
        </div>
      )}

      {/* 설문 섹션 1 */}
      {landingData.surveyTitle1 && survey1Answers.length > 0 && (
        <div className="surveySection">
          <SurveyQuestion
            topic={landingData.surveyTitle1}
            answers={survey1Answers}
            questionNumber={1}
            required={true}
            displayType={landingData.surveyTopic?.topic?.[0]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            onChange={setSurveyAnswer1}
          />
        </div>
      )}

      {/* 설문 섹션 2 */}
      {landingData.surveyTitle2 && survey2Answers.length > 0 && (
        <div className="surveySection">
          <SurveyQuestion
            topic={landingData.surveyTitle2}
            answers={survey2Answers}
            questionNumber={2}
            required={true}
            displayType={landingData.surveyTopic?.topic?.[1]?.type || 'radio'}
            numberColor={landingData.surveyTopic?.numberColor}
            answerColor={landingData.surveyAnswer?.answerColor}
            onChange={setSurveyAnswer2}
          />
        </div>
      )}

      {/* 이벤트 대상자 */}
      {landingData.eventTarget && (
        <div className="eventTarget">
          <p>이벤트 대상자 : <span className="event-target-text">{landingData.eventTarget}</span></p>
        </div>
      )}

      {/* 혜택 기간 */}
      {landingData.benefitDate && (
        <div className="benefitDate">
          <BenefitPeriod periodType={landingData.benefitDate} />
        </div>
      )}

      {/* 폼 섹션 */}
      <div className="formStyleClassName">
        <ZentorgaForm
          onSubmit={handleFormSubmit}
          isSubmitting={isSubmitting}
          formStyle={landingData.formStyleClassName || 'gray'}
          consentStyle={landingData.privacyPolicyClassName || 'dark'}
          privacyPolicyName={landingData.privacyPolicyName || '본원'}
          privacyPolicy={landingData.privacyPolicy}
          marketingPolicy={landingData.marketingPolicy}
          buttonComponent={
            <button
              ref={formRef}
              type="button"
              className={`postBtn ${landingData.postBtnLocate === 'fixed' ? 'postBtn-fixed' : ''}`}
              id="postBtn"
              disabled={isSubmitting}
              onClick={() => {
                if (landingData.postBtnLocate === 'fixed') {
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }
                formRef.current?.submitForm();  // ← requestSubmit() 대신 직접 호출
              }}
            >
              {landingData.postBtnUrl ? (
                <img src={landingData.postBtnUrl} alt="신청하기" />
              ) : (
                <span>{isSubmitting ? '신청 중...' : '신청하기'}</span>
              )}
            </button>
          }
        />
      </div>

      {/* 서비스 정보 */}
      {landingData.serviceInfoClassName && (
        <p
          className={`serviceInfoClassName ${landingData.serviceInfoClassName}`}
          id="serviceInfoClassName"
        >
          * 본 서비스는 전국 20여 개 이상의 제휴 병원에서 제공되며,<br />
          상담 신청 정보를 기반으로 전문 상담원이 적합한 제휴 병원을 안내해드립니다.
        </p>
      )}

      {/* 푸터 */}
      {landingData.footerClassName !== null && (landingData.footerClassName !== undefined || landingData.footerDetail) && (
        <footer className={`footerClassName ${landingData.footerClassName || ''}`}>
          {landingData.footerDetail && (
            <p className="footerDetail" style={{ whiteSpace: 'pre-line' }}>
              {landingData.footerDetail}
            </p>
          )}
        </footer>
      )}
    </section>
  );
};

export default Template13;
